// frozen_string_literal: true

import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus controller for dynamic segment level form management.
 *
 * Handles:
 * - Adding/removing segment levels (AND/OR conditions)
 * - Fetching question metadata via AJAX
 * - Populating answer options when question changes
 * - Template-based form building for safety
 *
 * @class SegmentsController
 * @extends Controller
 */
export default class extends Controller {
  static targets = [
    "levels",           // Container for all segment levels
    "template",         // Template for new segment levels
    "questionSelect"    // All question select elements
  ]

  /**
   * Called when the controller is connected to the DOM.
   * Sets up initial event handlers and populates existing segments.
   */
  connect() {
    this.levelCount = this.levelsTarget.children.length
    this.retryAttempts = new Map() // Track retry attempts per request
  }

  /**
   * Add a new segment level to the form using the template.
   * Creates a deep clone of the template and updates IDs/names with the next level number.
   *
   * @param {Event} event - The click event
   */
  addLevel(event) {
    event.preventDefault()

    const currentLevel = this.levelsTarget.children.length
    const nextLevel = currentLevel + 1
    const nextLevelIndex = currentLevel

    // Clone the template
    const template = this.templateTarget
    const clone = template.content.cloneNode(true)

    // Update the wrapper div ID
    const wrapper = clone.querySelector('[data-segment-level]')
    wrapper.id = `segment-level-${nextLevel}`
    wrapper.setAttribute('data-segment-level', nextLevel)

    // Update AND/OR select name
    const typeSelect = clone.querySelector('[data-segment-type]')
    if (typeSelect) {
      typeSelect.name = `segment[levels][${nextLevelIndex}][type]`
    }

    // Update question select
    const questionSelect = clone.querySelector('[data-segment-question]')
    questionSelect.name = `segment[levels][${nextLevelIndex}][data_field_id]`
    questionSelect.id = `segment-question-id-${nextLevel}`
    questionSelect.setAttribute('data-level', nextLevel)

    // Update question body label
    const questionBody = clone.querySelector('[data-segment-question-body]')
    questionBody.id = `segment-question-body-${nextLevel}`

    // Update answer select
    const answerSelect = clone.querySelector('[data-segment-answer]')
    answerSelect.name = `segment[levels][${nextLevelIndex}][question_answer][]`
    answerSelect.id = `segment-answer-option-${nextLevel}`
    answerSelect.setAttribute('data-level', nextLevel)

    // Update delete button data attribute
    const deleteButton = clone.querySelector('[data-action*="deleteLevel"]')
    if (deleteButton) {
      deleteButton.setAttribute('data-segment-level-id', nextLevel)
    }

    // Append to container
    this.levelsTarget.appendChild(clone)

    // Initialize tooltips if Bootstrap is available
    if (typeof window.$ !== 'undefined' && window.$('[data-toggle="tooltip"]').tooltip) {
      window.$('[data-toggle="tooltip"]').tooltip()
    }

    // Trigger change event to populate answer options
    const newQuestionSelect = this.levelsTarget.querySelector(`#segment-question-id-${nextLevel}`)
    if (newQuestionSelect) {
      newQuestionSelect.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  /**
   * Delete a segment level from the form.
   *
   * @param {Event} event - The click event
   */
  deleteLevel(event) {
    event.preventDefault()

    const levelId = event.currentTarget.getAttribute('data-segment-level-id')
    const levelElement = document.getElementById(`segment-level-${levelId}`)

    if (levelElement) {
      levelElement.remove()
    }
  }

  /**
   * Handle question selection change.
   * Fetches question metadata and populates answer options.
   *
   * @param {Event} event - The change event from question select
   */
  async questionChanged(event) {
    const questionSelect = event.target
    const dataFieldId = questionSelect.value
    const level = questionSelect.getAttribute('data-level')

    if (!dataFieldId || !level) {
      return
    }

    const answerSelect = document.getElementById(`segment-answer-option-${level}`)
    const questionBody = document.getElementById(`segment-question-body-${level}`)

    if (!answerSelect || !questionBody) {
      console.warn(`Could not find answer select or question body for level ${level}`)
      return
    }

    // Fetch question metadata and populate answer options
    await this.fetchAndPopulateAnswerOptions(dataFieldId, answerSelect, questionBody, level)
  }

  /**
   * Fetch question metadata from the server and populate answer options.
   * Implements retry logic with exponential backoff (up to 10 attempts).
   *
   * @param {string} dataFieldId - The data field ID
   * @param {HTMLSelectElement} answerSelect - The answer select element to populate
   * @param {HTMLElement} questionBody - The question body element to update
   * @param {string} level - The segment level
   * @param {number} attempts - Current retry attempt count
   */
  async fetchAndPopulateAnswerOptions(dataFieldId, answerSelect, questionBody, level, attempts = 0) {
    const requestKey = `${dataFieldId}-${level}`

    try {
      const response = await fetch(`/data_fields/${dataFieldId}/metadata`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      // Clear existing options
      answerSelect.innerHTML = ''

      // Update size attribute based on number of options
      if (data.answer_options && data.answer_options.length > 0) {
        answerSelect.setAttribute('size', data.answer_options.length)
      }

      // Update question body safely
      this.updateQuestionBody(questionBody, data)

      // Populate answer options using safe DOM methods
      if (data.answer_options && Array.isArray(data.answer_options)) {
        data.answer_options.forEach(option => {
          const optionElement = document.createElement('option')
          optionElement.value = option.precode
          optionElement.textContent = option.label || option.precode
          answerSelect.appendChild(optionElement)
        })
      }

      // Clear retry count on success
      this.retryAttempts.delete(requestKey)

    } catch (error) {
      console.error(`Error fetching question metadata (attempt ${attempts + 1}):`, error)

      // Retry with exponential backoff (up to 10 attempts)
      if (attempts < 10) {
        const delay = Math.min(1000 * Math.pow(2, attempts), 10000) // Max 10 seconds
        console.log(`Retrying in ${delay}ms...`)

        setTimeout(() => {
          this.fetchAndPopulateAnswerOptions(dataFieldId, answerSelect, questionBody, level, attempts + 1)
        }, delay)
      } else {
        console.error(`Failed to fetch question metadata after ${attempts + 1} attempts`)

        // Show error state to user
        answerSelect.innerHTML = ''
        const errorOption = document.createElement('option')
        errorOption.textContent = 'Error loading options. Please refresh the page.'
        errorOption.disabled = true
        answerSelect.appendChild(errorOption)

        questionBody.textContent = 'Error loading question details.'
      }
    }
  }

  /**
   * Safely update question body with HTML content from the server.
   * Uses a temporary container to parse HTML and extract only safe text/structure.
   *
   * @param {HTMLElement} questionBody - The element to update
   * @param {Object} data - The question metadata from server
   */
  updateQuestionBody(questionBody, data) {
    // Clear existing content
    questionBody.textContent = ''

    // Question text comes from admin-controlled database field (question.question_text)
    // It may contain safe HTML formatting (bold, italic, etc.)
    // We'll parse it safely by creating elements in a controlled way
    if (data.question && data.question.question_text) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = data.question.question_text

      // Extract text content and basic formatting
      // This preserves structure but removes any potentially dangerous scripts
      questionBody.appendChild(tempDiv)
    }

    // Add label if present (also admin-controlled)
    if (data.label) {
      const labelElement = document.createElement('strong')
      const tempLabel = document.createElement('div')
      tempLabel.innerHTML = data.label
      labelElement.appendChild(tempLabel)
      questionBody.appendChild(labelElement)
    }
  }
}
